import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Input, message, Modal, Row, Select, Space, Spin, Tooltip, Typography } from 'antd';
import StandardTable from '../../components/common/StandardTable';
import { api, fetchSqlData, updateDataBatch, deleteData, insertHeaderWithItems } from '../../services/api';
import { useAuth } from '../../components/common/security/AuthContext';
import FilePreviewModal, { canPreviewFile } from '../../components/common/FilePreviewModal';
import SelectDCCFileModal from '../../components/modals/SelectDCCFileModal';
import { dccDownloadUrl, dccPdfStatsRefresh, type DccListStatsItem } from '../../services/dccApi';
import type { DccTranslationLogEnrichedRow, DccTranslationStatus } from '../../types';
import { useGetDccTranslationLogEnriched } from '../../hooks/genericHooks';
import { queueNewTranslationLogRows } from '../../utils/dccTranslationQueue';
import Button from '../../components/common/Button';

const { Text } = Typography;

interface DccMhiDocsTranslationLogProps {
  selectedFolder?: string | null;
}

const normalizeStr = (v: any): string => String(v ?? '').trim();
/** Username compare helper: trim, strip common invisible chars, locale-insensitive lowercasing. */
const normalizeUsername = (v: any): string =>
  normalizeStr(v)
    .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
    .toLocaleLowerCase('en-US');
const normalizePath = (p: any): string => String(p ?? '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
const basename = (p: any): string => {
  const s = normalizePath(p);
  if (!s) return '';
  const parts = s.split('/').filter(Boolean);
  return parts[parts.length - 1] || s;
};

const dirname = (p: any): string => {
  const s = normalizePath(p);
  if (!s) return '';
  const parts = s.split('/').filter(Boolean);
  if (parts.length <= 1) return '';
  return parts.slice(0, parts.length - 1).join('/');
};

const formatDateTime = (v: any): string => {
  const s = normalizeStr(v);
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

const unwrapRows = <T,>(resp: any): T[] => {
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
};

const buildNextPackageId = (opts: {
  personnelId: string;
  username: string;
  existingPackages: Array<{ package_id?: any; created_by?: any }>;
}): string => {
  const personnelCode = String(opts.personnelId || '').trim();
  const username = String(opts.username || '').trim();
  if (!personnelCode) return '';

  const prefix = `${personnelCode}-`;
  const matches = (opts.existingPackages || [])
    .filter((p) => String(p?.created_by || '') === username)
    .map((p) => String(p?.package_id || ''))
    .filter((id) => id && id.startsWith(prefix));

  let maxNum = 0;
  let padWidth = 4;
  matches.forEach((id) => {
    const suffix = id.slice(prefix.length);
    const n = parseInt(suffix, 10);
    if (!Number.isNaN(n)) {
      if (suffix.length > padWidth) padWidth = suffix.length;
      if (n > maxNum) maxNum = n;
    }
  });

  const nextNum = maxNum + 1;
  return `${personnelCode}-${String(nextNum).padStart(padWidth, '0')}`;
};

const DccTranslationLog: React.FC<DccMhiDocsTranslationLogProps> = ({ selectedFolder }) => {
  const { token, user } = useAuth();
  const [rows, setRows] = useState<DccTranslationLogEnrichedRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const translationLogQ = useGetDccTranslationLogEnriched();
  const loading = translationLogQ.isLoading;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DccTranslationLogEnrichedRow[]>([]);

  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [isRePairOpen, setIsRePairOpen] = useState(false);
  const [rePairRow, setRePairRow] = useState<DccTranslationLogEnrichedRow | null>(null);

  const [statusFilter, setStatusFilter] = useState<DccTranslationStatus | undefined>(undefined);
  const [transmittalQuery, setTransmittalQuery] = useState('');
  const [query, setQuery] = useState('');
  const [requestedByQuery, setRequestedByQuery] = useState('');
  const [processedByQuery, setProcessedByQuery] = useState('');
  const [packageIdQuery, setPackageIdQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');

  const [dropPayload, setDropPayload] = useState<any | null>(null);
  const [sourceFilePath, setSourceFilePath] = useState('');
  const [saving, setSaving] = useState(false);

  const [packageOpen, setPackageOpen] = useState(false);
  const [packageFolders, setPackageFolders] = useState<string[]>([]);
  const [packageFolderItems, setPackageFolderItems] = useState<Record<string, DccListStatsItem[]>>({});
  const [packageLoading, setPackageLoading] = useState(false);
  const [translationPair, setTranslationPair] = useState('');
  const [supportSelectedKeys, setSupportSelectedKeys] = useState<React.Key[]>([]);
  const [supportSelectedRows, setSupportSelectedRows] = useState<Array<{ key: string; folder_path: string; file_path: string; name: string }>>([]);
  const [packageSaving, setPackageSaving] = useState(false);
  const [refreshingPdfStats, setRefreshingPdfStats] = useState(false);

  const baseFolder = useMemo(() => normalizePath(selectedFolder), [selectedFolder]);

  // Prefer live AuthContext username; fall back to localStorage during session hydrate.
  const currentUser = useMemo(
    () => normalizeStr(user || localStorage.getItem('user') || ''),
    [user],
  );

  const reload = async () => {
    try {
      setError(null);
      translationLogQ.refetch();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to reload translation log');
    }
  };

  const handleRefreshMissingPdfStats = async () => {
    if (!token) {
      message.error('No session');
      return;
    }
    const rootKey = (baseFolder.split('/')[0] || 'MHIDOCMAILS').trim();
    if (!rootKey) {
      message.warning('Select a folder root first');
      return;
    }
    try {
      setRefreshingPdfStats(true);
      const result = await dccPdfStatsRefresh(
        { root: rootKey, missing: true, recursive: false },
        token
      );
      const pending = result.pending_batch ?? 0;
      if (pending === 0) {
        message.info(`No missing PDF stats for root ${rootKey}`);
        return;
      }
      const ok = result.ok ?? 0;
      const failed = result.failed ?? 0;
      const written = result.written ?? 0;
      if (failed > 0) {
        message.warning(
          `PDF stats refresh (${rootKey}): ok=${ok}, failed=${failed}, written=${written} (${result.elapsed_ms ?? 0}ms)`
        );
      } else {
        message.success(
          `PDF stats refreshed for ${ok} file(s) under ${rootKey} (written=${written}, ${result.elapsed_ms ?? 0}ms)`
        );
      }
    } catch (e: any) {
      message.error(e?.message || 'Failed to refresh missing PDF stats');
    } finally {
      setRefreshingPdfStats(false);
    }
  };

  const openRePairModal = (row: DccTranslationLogEnrichedRow) => {
    const translated = normalizePath((row as any)?.translated_file_path);
    if (!translated) {
      message.info('This row has no translated file path to re-pair from');
      return;
    }
    setRePairRow(row);
    setIsRePairOpen(true);
  };

  const applyRePairSelection = async (args: { fullPath: string; name: string }) => {
    if (!rePairRow) return;
    const id = (rePairRow as any)?.id;
    if (id == null) return;

    const nextPath = normalizePath(args?.fullPath);
    if (!nextPath) return;

    try {
      await updateDataBatch('dcc_translation_log', {
        id_field: 'id',
        ids: [id] as any,
        data: {
          translated_file_path: nextPath,
          processed_by: currentUser || null,
        },
      });
      message.success('Re-paired translated file');
      setIsRePairOpen(false);
      setRePairRow(null);
      await reload();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to re-pair translated file');
    }
  };

  /** Same pattern as DccDocuments: preview when supported, otherwise download via DCC API. */
  const handleDownloadPath = (filePath: string) => {
    const s = normalizePath(filePath);
    if (!s) return;
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    const url = dccDownloadUrl(s, token, false);
    window.open(url, '_blank');
  };

  const handleOpenFilePath = (filePath: string) => {
    const s = normalizePath(filePath) || String(filePath ?? '').trim();
    if (!s) return;
    if (canPreviewFile(s)) {
      setPreviewPath(s);
      setIsPreviewOpen(true);
      return;
    }
    handleDownloadPath(s);
  };

  const renderFilePathCell = (v: any) => {
    const s = String(v ?? '');
    if (!s) return '';
    const pathForOpen = normalizePath(s) || s;
    const previewable = canPreviewFile(pathForOpen);
    return (
      <Tooltip
        title={previewable ? s : `${s} — Double-click to download`}
        placement="topLeft"
      >
        {previewable ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFilePath(pathForOpen);
            }}
          >
            {s}
          </Button>
        ) : (
          <span
            style={{ cursor: 'pointer' }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleDownloadPath(pathForOpen);
            }}
          >
            {s}
          </span>
        )}
      </Tooltip>
    );
  };

  useEffect(() => {
    const arr = (translationLogQ.data?.data || []) as DccTranslationLogEnrichedRow[];
    setRows(arr || []);
    if (translationLogQ.isError) {
      setError((translationLogQ.error as any)?.message || 'Failed to load translation log');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationLogQ.data, translationLogQ.isError]);

  const revokeQueuedByIds = async (ids: Array<number | string>) => {
    const cleanIds = (ids || []).filter((v) => typeof v === 'number' || (typeof v === 'string' && String(v).trim() !== ''));
    if (!cleanIds.length) return;
    try {
      for (const id of cleanIds) {
        await deleteData('dcc_translation_log', 'id', id as any);
      }
      message.success(`Removed ${cleanIds.length} queued row(s)`);
      await reload();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to revoke queued item(s)');
    }
  };

  const parseDropEvent = (ev: React.DragEvent): any | null => {
    const raw = ev.dataTransfer.getData('application/json') || ev.dataTransfer.getData('text/plain');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };

  const buildExternalTransmittalNo = (input: { folder_path?: any; source_file_path?: any }): string => {
    const folderPath = normalizePath(input?.folder_path ?? '');
    const srcPath = normalizePath(input?.source_file_path ?? '');

    const pickFolderName = (): string => {
      const fp = folderPath.replace(/\\/g, '/');
      if (fp) {
        const parts = fp.split('/').filter(Boolean);
        return parts[parts.length - 1] || '';
      }
      const sp = srcPath.replace(/\\/g, '/');
      if (sp) {
        const parts = sp.split('/').filter(Boolean);
        // parent folder of the file path
        return parts.length >= 2 ? (parts[parts.length - 2] || '') : '';
      }
      return '';
    };

    const rawName = pickFolderName();
    const cleaned = String(rawName || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 10);

    const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
    if (cleaned) return `TKF-EXT-${cleaned}-${suffix}`;
    return `TKF-EXT-${suffix}`;
  };

  const handleQueueDrop = async (payload: any) => {
    if (!payload) return;
    // Single item drop: {transmittal_no, document_no, revision, folder_path}
    // Multi drop: [{...}, {...}]
    const items = Array.isArray(payload) ? payload : [payload];
    const normalized = items
      .map((it) => ({
        transmittal_no: String(it?.transmittal_no ?? '').trim(),
        document_no: String(it?.document_no ?? '').trim(),
        revision: String(it?.revision ?? '').trim(),
        folder_path: normalizePath(it?.folder_path ?? ''),
      }))
      .filter((it) => it.document_no && it.folder_path);

    if (normalized.length === 0) {
      setError('Dropped items are missing required fields (document_no, folder_path).');
      return;
    }

    setDropPayload(normalized.length === 1 ? normalized[0] : normalized);
    setSourceFilePath('');
  };

  const saveQueuedItem = async () => {
    if (!dropPayload) return;
    try {
      setSaving(true);
      setError(null);

      const requestedBy = localStorage.getItem('user') || null;

      // Single row path
      if (!Array.isArray(dropPayload)) {
        const src = normalizePath(sourceFilePath);
        if (!src) {
          setError('Source file path is required.');
          return;
        }

        const transmittalNo = normalizeStr((dropPayload as any)?.transmittal_no) || buildExternalTransmittalNo({
          folder_path: (dropPayload as any)?.folder_path,
          source_file_path: src,
        });
        await queueNewTranslationLogRows([
          {
            transmittal_no: transmittalNo,
            document_no: dropPayload.document_no,
            revision: dropPayload.revision || '',
            folder_path: dropPayload.folder_path,
            source_file_path: src,
            status: 'queued',
            requested_by: requestedBy,
          },
        ]);
      } else {
        // Multi row path: source_file_path must be provided per row (unique key)
        const rows: Array<{
          transmittal_no: string;
          document_no: string;
          revision: string;
          folder_path: string;
          source_file_path: string;
          status: string;
          requested_by: string | null;
        }> = [];
        for (const it of dropPayload as any[]) {
          const src = normalizePath(it?.source_file_path);
          if (!src) {
            setError('Each row must have a source_file_path.');
            return;
          }
          const transmittalNo = normalizeStr(it?.transmittal_no) || buildExternalTransmittalNo({
            folder_path: it?.folder_path,
            source_file_path: src,
          });
          rows.push({
            transmittal_no: transmittalNo,
            document_no: String(it?.document_no ?? ''),
            revision: String(it?.revision ?? ''),
            folder_path: String(it?.folder_path ?? ''),
            source_file_path: src,
            status: 'queued',
            requested_by: requestedBy,
          });
        }

        await queueNewTranslationLogRows(rows);
      }

      setDropPayload(null);
      setSourceFilePath('');
      await reload();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to queue item');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder]);

  const filteredRows = useMemo(() => {
    let out = rows || [];

    // All active filters below are applied sequentially with AND semantics.
    // Within each text filter, space-separated words also AND-match (every term must hit).

    if (statusFilter) {
      out = out.filter((r) => String(r.status || '').toLowerCase() === String(statusFilter).toLowerCase());
    }

    const tq = String(transmittalQuery || '').trim().toLowerCase();
    if (tq) {
      const terms = tq.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const trans = String((r as any)?.transmittal_no ?? '').toLowerCase();
        return terms.every((t) => trans.includes(t));
      });
    }

    const pq = String(packageIdQuery || '').trim().toLowerCase();
    if (pq) {
      const terms = pq.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const pkg = String((r as any)?.package_id ?? '').toLowerCase();
        return terms.every((t) => pkg.includes(t));
      });
    }

    const q = String(query || '').trim().toLowerCase();
    if (q) {
      const terms = q.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const hay = [
          r.transmittal_no,
          r.document_no,
          r.revision,
          r.folder_path,
          r.source_file_path,
          r.translated_file_path,
          r.requested_by,
          r.vendor,
          r.notes,
          (r as any)?.package_id,
        ]
          .map((v) => String(v ?? '').toLowerCase())
          .join(' ');
        return terms.every((t) => hay.includes(t));
      });
    }

    const rbq = String(requestedByQuery || '').trim().toLowerCase();
    if (rbq) {
      const terms = rbq.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const s = String((r as any)?.requested_by ?? '').toLowerCase();
        return terms.every((t) => s.includes(t));
      });
    }

    const pbq = String(processedByQuery || '').trim().toLowerCase();
    if (pbq) {
      const terms = pbq.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const s = String((r as any)?.processed_by ?? '').toLowerCase();
        return terms.every((t) => s.includes(t));
      });
    }

    const vf = String(vendorFilter || '').trim().toLowerCase();
    if (vf) {
      const terms = vf.split(/\s+/).filter(Boolean);
      out = out.filter((r) => {
        const s = String((r as any)?.vendor ?? '').toLowerCase();
        return terms.every((t) => s.includes(t));
      });
    }

    return out;
  }, [packageIdQuery, query, processedByQuery, requestedByQuery, rows, statusFilter, transmittalQuery, vendorFilter]);

  const statusOptions = useMemo(
    () => [
      { value: 'queued', label: 'queued' },
      { value: 'packed', label: 'packed' },
      { value: 'sent', label: 'sent' },
      { value: 'completed', label: 'completed' },
      { value: 'cancelled', label: 'cancelled' },
    ],
    [],
  );

  const selectedQueuedIds = useMemo(() => {
    const cu = normalizeStr(currentUser).toLowerCase();
    return (selectedRows || [])
      .filter((r: any) => String(r?.status || '').toLowerCase() === 'queued')
      .filter((r: any) => normalizeStr(r?.requested_by).toLowerCase() === cu)
      .map((r: any) => r?.id)
      .filter((v: any) => typeof v === 'number' || (typeof v === 'string' && String(v).trim() !== ''));
  }, [currentUser, selectedRows]);

  const vendorOptions = useMemo(() => {
    const uniq = new Set<string>();
    (rows || []).forEach((r) => {
      const v = normalizeStr((r as any)?.vendor);
      if (v) uniq.add(v);
    });
    return Array.from(uniq).sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  }, [rows]);

  const selectedQueuedBlockedCount = useMemo(() => {
    const cu = normalizeStr(currentUser).toLowerCase();
    return (selectedRows || [])
      .filter((r: any) => String(r?.status || '').toLowerCase() === 'queued')
      .filter((r: any) => normalizeStr(r?.requested_by).toLowerCase() !== cu)
      .length;
  }, [currentUser, selectedRows]);

  const columns: any[] = [
    { title: 'Trans #', dataIndex: 'transmittal_no', key: 'transmittal_no', width: 120 },
    { title: 'Doc #', dataIndex: 'document_no', key: 'document_no', width: 320},
    { title: 'Rev', dataIndex: 'revision', key: 'revision', width: 55 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 80 },
    {
      title: 'Package',
      dataIndex: 'package_id',
      key: 'package_id',
      width: 120,
      render: (v: any) => normalizeStr(v) || '',
    },
    {
      title: 'Pages',
      dataIndex: 'page_count',
      key: 'page_count',
      width: 66,
      align: 'right',
      render: (v: any) => <span style={{ display: 'inline-block', width: '100%', textAlign: 'right' }}>{v ?? ''}</span>,
    },
    {
      title: 'Chars',
      dataIndex: 'char_count',
      key: 'char_count',
      width: 68,
      align: 'right',
      render: (v: any) => <span style={{ display: 'inline-block', width: '100%', textAlign: 'right' }}>{v ?? ''}</span>,
    },
    {
      title: 'RunOCR',
      dataIndex: 'needs_ocr',
      key: 'needs_ocr',
      width: 75,
      render: (v: any) => {
        if (v === null || typeof v === 'undefined') return '';
        return String(v) === 'true' || v === true ? 'YES' : 'NO';
      },
    },
    {
      title: 'Source file',
      dataIndex: 'source_file_path',
      key: 'source_file_path',
      width: 180,
      render: renderFilePathCell,
    },
    {
      title: 'Translated file',
      dataIndex: 'translated_file_path',
      key: 'translated_file_path',
      width: 250,
      render: renderFilePathCell,
    },
    { title: 'Requested by', dataIndex: 'requested_by', key: 'requested_by', width: 120 },
    { title: 'Processed by', dataIndex: 'processed_by', key: 'processed_by', width: 120 },
    { title: 'Vendor', dataIndex: 'vendor', key: 'vendor', width: 120 },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (v: any) => {
        const raw = v;
        const txt = formatDateTime(raw);
        if (!txt) return '';
        return (
          <Tooltip title={normalizeStr(raw)} placement="topLeft">
            <span>{txt}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 140,
      render: (v: any) => {
        const raw = v;
        const txt = formatDateTime(raw);
        if (!txt) return '';
        return (
          <Tooltip title={normalizeStr(raw)} placement="topLeft">
            <span>{txt}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: any, r: any) => {
        const translatedPath = normalizePath((r as any)?.translated_file_path);
        const cu = normalizeUsername(currentUser);
        const processedBy = normalizeUsername((r as any)?.processed_by);
        const canRePair = Boolean(cu) && processedBy === cu;

        if (!translatedPath) return null;

        return (
          <Space size={6}>
            <Tooltip
              title={
                !canRePair
                  ? 'Re-pairing can only be done by the processing user'
                  : 'Select a translated file from the same folder'
              }
            >
              <Button
                size="small"
                disabled={!canRePair}
                onClick={() => openRePairModal(r as any)}
              >
                Re-pair
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const selectedFolderPaths = useMemo(() => {
    const uniq = new Set<string>();
    (selectedRows || []).forEach((r) => {
      const fp = normalizePath((r as any)?.folder_path);
      if (fp) uniq.add(fp);
    });
    return Array.from(uniq);
  }, [selectedRows]);

  const canCreatePackage = useMemo(() => {
    if (!selectedRows.length) return false;
    return (selectedRows || []).every((r: any) => String(r?.status || '').toLowerCase() === 'queued');
  }, [selectedRows]);

  const primaryItemsByFolder = useMemo(() => {
    const out: Record<string, Array<{ key: string; folder_path: string; file_path: string; name: string }>> = {};
    (selectedRows || []).forEach((r) => {
      const folderPath = normalizePath((r as any)?.folder_path);
      const filePath = normalizePath((r as any)?.source_file_path);
      if (!folderPath || !filePath) return;
      if (!out[folderPath]) out[folderPath] = [];
      out[folderPath].push({
        key: `${folderPath}::${filePath}`,
        folder_path: folderPath,
        file_path: filePath,
        name: basename(filePath),
      });
    });
    return out;
  }, [selectedRows]);

  const supportingCandidatesByFolder = useMemo(() => {
    const out: Record<string, Array<{ key: string; folder_path: string; file_path: string; name: string }>> = {};
    (packageFolders || []).forEach((folderPath) => {
      const items = packageFolderItems[folderPath] || [];
      const rowsForFolder = items
        .filter((it) => !Boolean((it as any)?.is_dir))
        .map((it) => {
          const rawPath = normalizePath((it as any)?.path || (it as any)?.name);
          const looksLikeFullPath = rawPath && (rawPath.toLowerCase().startsWith(`${folderPath.toLowerCase()}/`) || rawPath.toLowerCase() === folderPath.toLowerCase());
          const fullPath = rawPath
            ? (looksLikeFullPath
                ? rawPath
                : `${normalizePath(folderPath)}/${rawPath.replace(/^\/+/, '')}`)
            : '';
          const name = String((it as any)?.name || basename(fullPath) || '');
          return {
            key: `${folderPath}::${fullPath}`,
            folder_path: folderPath,
            file_path: fullPath,
            name,
          };
        })
        .filter((r) => {
          if (!r.file_path) return false;
          const lower = r.name.toLowerCase();
          const isPdf = lower.endsWith('.pdf');
          const isMsg = lower.endsWith('.msg');
          const isTxt = lower.endsWith('.txt');
          return !isPdf && !isMsg && !isTxt;
        });

      out[folderPath] = rowsForFolder;
    });
    return out;
  }, [packageFolderItems, packageFolders]);

  const supportingKeysByFolder = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    (supportSelectedRows || []).forEach((r) => {
      const folderPath = normalizePath(r.folder_path);
      const key = String(r.key);
      if (!folderPath || !key) return;
      if (!out[folderPath]) out[folderPath] = new Set<string>();
      out[folderPath].add(key);
    });
    return out;
  }, [supportSelectedRows]);

  const loadPackageFolders = async (folders: string[]) => {
    if (!folders.length) return;
    setPackageLoading(true);
    try {
      const requested = folders.map((f) => normalizePath(f)).filter(Boolean);
      const results: Record<string, DccListStatsItem[]> = {};
      await Promise.all(
        requested.map(async (folderPath) => {
          const resp = await api.get('/dcc/list_stats', {
            params: { path: folderPath, storage_root: 'mhidocmails' },
          });
          const stats: DccListStatsItem[] = Array.isArray(resp?.data) ? resp.data : resp?.data?.data || [];
          results[folderPath] = stats || [];
        })
      );
      setPackageFolderItems(results);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load folder contents for package');
    } finally {
      setPackageLoading(false);
    }
  };

  const openCreatePackage = async () => {
    if (!selectedRows.length) return;
    const folders = selectedFolderPaths;
    setPackageFolders(folders);
    setSupportSelectedKeys([]);
    setSupportSelectedRows([]);
    setPackageFolderItems({});
    setTranslationPair('');
    setPackageOpen(true);
    await loadPackageFolders(folders);
  };

  const savePackage = async () => {
    if (!selectedRows.length) {
      setPackageOpen(false);
      return;
    }
    try {
      setPackageSaving(true);
      const createdBy = localStorage.getItem('user') || null;
      // This is the personnel code (personnel.personnel_id) persisted by AuthContext
      const personnelId = localStorage.getItem('personnel_id') || '';
      const pair = String(translationPair || '').trim().toUpperCase();
      if (!createdBy) {
        throw new Error('Not authenticated');
      }
      if (!personnelId) {
        throw new Error('Cannot infer personnel_id (personnel code) for package_id generation');
      }
      if (!pair) {
        throw new Error('Translation pair is required');
      }

      const pkData = await fetchSqlData('dcc_translation_packages');
      const pkArr: Array<{ package_id?: any; created_by?: any }> = Array.isArray(pkData) ? pkData : pkData?.data || [];
      const packageId = buildNextPackageId({
        personnelId,
        username: createdBy,
        existingPackages: pkArr,
      });
      if (!packageId) {
        throw new Error('Failed to generate package_id');
      }

      const pdfItems = selectedRows.map((r) => ({
        package_id: packageId,
        file_path: normalizePath((r as any)?.source_file_path),
        role: 'primary',
        translation_log_id: (r as any)?.id,
      })).filter((it) => it.file_path);

      const supportItems = supportSelectedRows.map((r) => ({
        package_id: packageId,
        file_path: normalizePath(r.file_path),
        role: 'supporting',
        translation_log_id: null,
      })).filter((it) => it.file_path);

      const allItems = [...pdfItems, ...supportItems];
      if (!allItems.length) {
        throw new Error('Package has no items');
      }

      const resp = await insertHeaderWithItems({
        header_table: 'dcc_translation_packages',
        header_data: {
          package_id: packageId,
          created_by: createdBy,
          status: 'draft',
          folder_count: packageFolders.length,
          file_count_primary: pdfItems.length,
          file_count_supporting: supportItems.length,
          translationpair: pair,
          sent_date: null,
          sender: createdBy,
        },
        items_table: 'dcc_translation_package_items',
        items: allItems,
      });

      const headerId = (resp as any)?.header_id;
      message.success(
        `Package created (package_id: ${packageId}${headerId ? `, id: ${headerId}` : ''}) with ${allItems.length} item(s)`
      );

      const primaryLogIds = (selectedRows || [])
        .map((r) => (r as any)?.id)
        .filter((v) => typeof v === 'number' || (typeof v === 'string' && String(v).trim() !== ''));
      if (primaryLogIds.length) {
        try {
          await updateDataBatch('dcc_translation_log', {
            id_field: 'id',
            ids: primaryLogIds as any,
            data: { status: 'packed', processed_by: createdBy },
          });
          await reload();
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || 'Package created but failed to mark items as packed');
        }
      }
      setPackageOpen(false);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to create package');
    } finally {
      setPackageSaving(false);
    }
  };

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {baseFolder && (
          <Text type="secondary">
            Folder scope: <Text code>{baseFolder}</Text>
          </Text>
        )}

        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={4}>
            <Input
              allowClear
              placeholder="Transmittal #"
              value={transmittalQuery}
              onChange={(e) => setTransmittalQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={4}>
            <Input
              allowClear
              placeholder="Search (doc/path/vendor...)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={3}>
            <Input
              allowClear
              placeholder="Requested by"
              value={requestedByQuery}
              onChange={(e) => setRequestedByQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={3}>
            <Input
              allowClear
              placeholder="Processed by"
              value={processedByQuery}
              onChange={(e) => setProcessedByQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={3}>
            <Input
              allowClear
              placeholder="Package #"
              value={packageIdQuery}
              onChange={(e) => setPackageIdQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              allowClear
              showSearch
              placeholder="Vendor"
              value={vendorFilter || undefined}
              onChange={(v) => setVendorFilter((v as string) || '')}
              options={vendorOptions}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(String(input).toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={2}>
            <Select
              allowClear
              placeholder="Status"
              value={statusFilter as any}
              onChange={(v) => setStatusFilter((v as any) || undefined)}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        <Row gutter={[8, 8]} align="middle">
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space size={8} wrap>
                <Button action="add" type="primary" disabled={!canCreatePackage} onClick={openCreatePackage}>
                  Create package
                </Button>
                <Button
                  danger
                  disabled={selectedQueuedIds.length === 0}
                  onClick={() => {
                    if (!currentUser) {
                      message.warning('Cannot infer current user; revoke queued is disabled');
                      return;
                    }
                    if (selectedQueuedBlockedCount > 0) {
                      message.warning('Some selected queued rows were requested by a different user and cannot be revoked');
                      return;
                    }
                    Modal.confirm({
                      title: 'Revoke queued (bulk)',
                      content: `This will remove ${selectedQueuedIds.length} queued row(s) from the translation log. Continue?`,
                      okText: 'Remove',
                      okButtonProps: { danger: true },
                      onOk: async () => revokeQueuedByIds(selectedQueuedIds as any),
                    });
                  }}
                >
                  Revoke queued
                </Button>
                <Button onClick={handleRefreshMissingPdfStats} loading={refreshingPdfStats}>
                  Refresh PDF stats
                </Button>
                <Button onClick={reload} loading={loading}>
                  Reload
                </Button>
              </Space>
            </div>
          </Col>
        </Row>

        {error && <Alert type="error" message="Failed to load translation log" description={error} showIcon />}

        <Spin spinning={loading}>
          <div
            style={{ width: '100%' }}
            onDragOver={(ev) => {
              ev.preventDefault();
              ev.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(ev) => {
              ev.preventDefault();
              const payload = parseDropEvent(ev);
              handleQueueDrop(payload);
            }}
          >
            <StandardTable
              columns={columns}
              dataSource={filteredRows}
              rowKey={(r: any) => String(r.id)}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys, selected) => {
                  setSelectedRowKeys(keys);
                  setSelectedRows(selected as DccTranslationLogEnrichedRow[]);
                },
              }}
              size="small"
              style={{ width: '100%' }}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </div>
        </Spin>

        <FilePreviewModal
          open={isPreviewOpen}
          path={previewPath}
          token={token}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewPath(null);
          }}
          width="85%"
        />

        <SelectDCCFileModal
          open={isRePairOpen}
          initialPath={dirname((rePairRow as any)?.translated_file_path)}
          mode="single"
          onCancel={() => {
            setIsRePairOpen(false);
            setRePairRow(null);
          }}
          onSelect={applyRePairSelection}
        />

        <Modal
          title="Queue document for translation"
          open={Boolean(dropPayload)}
          onCancel={() => {
            setDropPayload(null);
            setSourceFilePath('');
          }}
          onOk={saveQueuedItem}
          okText={Array.isArray(dropPayload) ? 'Queue all' : 'Queue'}
          confirmLoading={saving}
          destroyOnClose
        >
          {!Array.isArray(dropPayload) ? (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text type="secondary">
                Dragged:
                {' '}
                <Text code>
                  {dropPayload?.transmittal_no} / {dropPayload?.document_no} / {String(dropPayload?.revision || '')}
                </Text>
              </Text>
              <Text type="secondary">
                Folder:
                {' '}
                <Text code>{dropPayload?.folder_path}</Text>
              </Text>
              <Input
                value={sourceFilePath}
                onChange={(e) => setSourceFilePath(e.target.value)}
                placeholder="Source file path (relative DCC path)"
                allowClear
              />
              <Text type="secondary">
                Tip: open <Text code>/dcc/documents</Text>, locate the PDF, and copy its path.
              </Text>
            </Space>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text type="secondary">
                Dragged <Text code>{dropPayload.length}</Text> rows. Fill source file paths below.
              </Text>
              <StandardTable
                enableExport={false}
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                columns={[
                  { title: 'Trans #', dataIndex: 'transmittal_no', key: 'transmittal_no', width: 120},
                  { title: 'Doc #', dataIndex: 'document_no', key: 'document_no', width: 140 },
                  { title: 'Rev', dataIndex: 'revision', key: 'revision', width: 60 },
                  {
                    title: 'Source file path',
                    dataIndex: 'source_file_path',
                    key: 'source_file_path',
                    width: 420,
                    render: (_: any, rec: any, idx: number) => (
                      <Input
                        value={String(rec?.source_file_path ?? '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDropPayload((prev: any) => {
                            if (!Array.isArray(prev)) return prev;
                            const next = [...prev];
                            next[idx] = { ...next[idx], source_file_path: val };
                            return next;
                          });
                        }}
                        placeholder="MHIDOCMAILS/..."
                        allowClear
                      />
                    ),
                  },
                ]}
                dataSource={dropPayload.map((r: any, i: number) => ({ ...r, key: String(i) }))}
                rowKey={(r: any) => String(r.key)}
                size="small"
                scroll={{ x: 'max-content' }}
              />
              <Text type="secondary">
                Tip: if most PDFs share a naming pattern, paste paths quickly (one per row).
              </Text>
            </Space>
          )}
        </Modal>

        <Modal
          title="Create translation package"
          open={packageOpen}
          onCancel={() => setPackageOpen(false)}
          onOk={savePackage}
          okText="Create package"
          confirmLoading={packageSaving}
          okButtonProps={{ disabled: !String(translationPair || '').trim() }}
          width={980}
          destroyOnClose
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text type="secondary">
              PDFs in package:
              {' '}
              <Text code>{selectedRows.length}</Text>
              {' '}
              | Supporting files selected:
              {' '}
              <Text code>{supportSelectedRows.length}</Text>
            </Text>

            <Row gutter={[8, 8]} align="middle">
              <Col xs={24} md={10}>
                <Input
                  value={translationPair}
                  onChange={(e) => setTranslationPair(e.target.value)}
                  placeholder="Translation pair (e.g. EN-RU)"
                  maxLength={5}
                  allowClear
                />
              </Col>
            </Row>

            <Spin spinning={packageLoading}>
              <Row gutter={[12, 12]}>
                {packageFolders.map((folderPath) => {
                  const primary = primaryItemsByFolder[folderPath] || [];
                  const supporting = supportingCandidatesByFolder[folderPath] || [];
                  const folderSelectedKeys = Array.from(supportingKeysByFolder[folderPath] || new Set<string>());

                  return (
                    <Col xs={24} key={folderPath}>
                      <Card size="small" title={<Text code>{folderPath}</Text>} bodyStyle={{ padding: 10 }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <div>
                            <Text type="secondary">Primary PDFs (auto-included): </Text>
                            <Text code>{primary.length}</Text>
                          </div>
                          <StandardTable
                            enableExport={false}
                            enableColumnTools={false}
                            enableResetView={false}
                            size="small"
                            pagination={false}
                            rowKey={(r: any) => String(r.key)}
                            columns={[
                              {
                                title: 'PDF',
                                dataIndex: 'name',
                                key: 'name',
                                width: 240,
                                render: (_: any, r: any) => (
                                  <Tooltip title={String(r?.file_path || '')} placement="topLeft">
                                    <span>{String(r?.name || '')}</span>
                                  </Tooltip>
                                ),
                              },
                            ]}
                            dataSource={primary}
                          />

                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">Supporting files (select): </Text>
                            <Text code>{folderSelectedKeys.length}</Text>
                          </div>
                          <StandardTable
                            enableExport={false}
                            enableColumnTools={false}
                            enableResetView={false}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            pagination={false}
                            rowKey={(r: any) => String(r.key)}
                            rowSelection={{
                              selectedRowKeys: folderSelectedKeys,
                              onChange: (keys, selected) => {
                                const nextSelected = (selected as any[]).map((r) => ({
                                  key: String(r?.key),
                                  folder_path: String(r?.folder_path),
                                  file_path: String(r?.file_path),
                                  name: String(r?.name),
                                }));

                                setSupportSelectedRows((prev) => {
                                  const remaining = (prev || []).filter((r) => normalizePath(r.folder_path) !== normalizePath(folderPath));
                                  return [...remaining, ...nextSelected];
                                });
                                setSupportSelectedKeys((prev) => {
                                  const remaining = (prev || []).filter((k) => !String(k).startsWith(`${folderPath}::`));
                                  return [...remaining, ...keys];
                                });
                              },
                            }}
                            columns={[
                              {
                                title: 'Name',
                                dataIndex: 'name',
                                key: 'name',
                                width: 260,
                                render: (_: any, r: any) => (
                                  <Tooltip title={String(r?.file_path || '')} placement="topLeft">
                                    <span>{String(r?.name || '')}</span>
                                  </Tooltip>
                                ),
                              },
                            ]}
                            dataSource={supporting}
                          />
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Spin>
          </Space>
        </Modal>
      </Space>
    </Card>
  );
};

export default DccTranslationLog;
